import { useEffect, useRef, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Smartphone, RefreshCcw, LogOut, Send, ExternalLink, Copy, Check, MessageCircle, Save, Trash2, Eye, EyeOff, Zap, ZapOff, TestTube2, Info } from "lucide-react";

const inp =
  "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none text-sm";

const STATE_STYLE = {
  connected: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  connecting: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  not_configured: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  error: "text-red-400 bg-red-500/10 border-red-500/30",
};

/** Monta um link wa.me com número + mensagem pré-preenchida (Click-to-Chat). */
export function buildWaMeLink(phone, text) {
  const digits = String(phone || "").replace(/\D/g, "");
  const withCC = digits.length <= 11 ? `55${digits}` : digits;
  const encoded = encodeURIComponent(text || "");
  return `https://wa.me/${withCC}${encoded ? `?text=${encoded}` : ""}`;
}

export default function WhatsApp() {
  const [tab, setTab] = useState("baileys"); // baileys | meta | wame
  // Baileys state
  const [bail, setBail] = useState({ connected: false, connecting: false, qr: null, phone: null, has_session: false, last_error: null });
  const [starting, setStarting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  // Meta state (integração oficial)
  const [meta, setMeta] = useState({ connected: false, state: "not_configured", configured: false });
  const [templates, setTemplates] = useState([]);
  const [tplError, setTplError] = useState(null);
  // Enviar teste (formulário compartilhado)
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("Teste MercadoPagu ✅");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const refreshStatus = async () => {
    try {
      const { data } = await api.get("/whatsapp/status");
      setBail(data.baileys || {});
      setMeta(data.meta || {});
    } catch (_e) {
      // silent
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data.templates || []);
      setTplError(data.error || null);
    } catch (e) {
      setTplError(e?.response?.data?.detail || "Falha ao carregar templates");
    }
  };

  useEffect(() => {
    refreshStatus();
    loadTemplates();
    // Poll a cada 3s enquanto não estiver conectado (para pegar QR novo / status)
    pollRef.current = setInterval(() => {
      refreshStatus();
    }, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const startBaileys = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/whatsapp/session/start");
      setBail((s) => ({ ...s, ...data }));
      toast.success("Sessão iniciada. Escaneie o QR Code com seu WhatsApp.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar sessão");
    } finally {
      setStarting(false);
    }
  };

  const logoutBaileys = async () => {
    if (!confirm("Desconectar o WhatsApp? Você precisará escanear o QR de novo.")) return;
    setLoggingOut(true);
    try {
      await api.post("/whatsapp/session/logout");
      setBail({ connected: false, connecting: false, qr: null, phone: null });
      toast.success("Desconectado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao desconectar");
    } finally {
      setLoggingOut(false);
    }
  };

  const sendBaileys = async () => {
    if (!testTo.trim() || !testMsg.trim()) return toast.error("Preencha número e mensagem");
    setSending(true);
    try {
      await api.post("/whatsapp/baileys/send", { to: testTo, text: testMsg });
      toast.success("Mensagem enviada!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const sendMeta = async () => {
    if (!testTo.trim() || !testMsg.trim()) return toast.error("Preencha número e mensagem");
    setSending(true);
    try {
      await api.post("/whatsapp/send", { to: testTo, message: testMsg });
      toast.success("Enviado via Meta!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar via Meta");
    } finally {
      setSending(false);
    }
  };

  const bailStateLabel = bail.connected ? "connected" : bail.connecting ? "connecting" : "not_configured";
  const bailStateText = bail.connected ? `Conectado (${bail.phone || "-"})` : bail.connecting ? "Aguardando escaneamento" : "Desconectado";

  const waMeLink = useMemo(() => buildWaMeLink(testTo, testMsg), [testTo, testMsg]);
  const copyWaMe = async () => {
    try {
      await navigator.clipboard.writeText(waMeLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="whatsapp-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-black tracking-tight">WhatsApp</h1>
          <p className="text-sm opacity-70 mt-1">
            Conecte via QR Code (Baileys, sem Chromium), use a API oficial da Meta ou botões wa.me para conversas rápidas.
          </p>
        </div>
        <button
          onClick={refreshStatus}
          className="px-3 py-2 border border-[var(--border)] rounded-sm text-xs hover:bg-[var(--bg-elev)] flex items-center gap-2"
          data-testid="whatsapp-refresh-btn"
        >
          <RefreshCcw className="w-3 h-3" /> Atualizar
        </button>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {[
          { key: "baileys", label: "Conexão QR Code", badge: bail.connected ? "on" : bail.connecting ? "..." : null },
          { key: "meta", label: "API Meta Oficial", badge: meta.connected ? "on" : null },
          { key: "wame", label: "Link wa.me", badge: null },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 flex items-center gap-2 ${
              tab === t.key
                ? "border-[var(--accent)] text-[var(--accent)]"
                : "border-transparent opacity-70 hover:opacity-100"
            }`}
            data-testid={`whatsapp-tab-${t.key}`}
          >
            {t.label}
            {t.badge === "on" && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
            {t.badge === "..." && <span className="text-amber-400">…</span>}
          </button>
        ))}
      </div>

      {tab === "baileys" && (
        <section className="space-y-6" data-testid="whatsapp-baileys-panel">
          <div className="border border-[var(--border)] rounded-sm p-6 bg-[var(--bg-elev)] flex items-start gap-6 flex-wrap">
            <div className="w-64 h-64 border border-[var(--border)] rounded-sm bg-white flex items-center justify-center overflow-hidden">
              {bail.connected ? (
                <div className="text-center p-4">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500 flex items-center justify-center mb-3">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-black font-bold">Conectado</div>
                  <div className="text-slate-600 text-xs mt-1">{bail.phone}</div>
                </div>
              ) : bail.qr ? (
                <img src={bail.qr} alt="QR Code do WhatsApp" className="w-full h-full object-contain p-2" data-testid="whatsapp-qr-img" />
              ) : (
                <div className="text-center p-4 text-slate-500 text-sm">
                  <Smartphone className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  Clique em Iniciar para gerar o QR Code
                </div>
              )}
            </div>

            <div className="flex-1 min-w-[260px] space-y-3">
              <div className={`inline-flex items-center gap-2 border px-3 py-1 rounded-sm text-xs ${STATE_STYLE[bailStateLabel]}`} data-testid="whatsapp-baileys-status">
                <span className="w-2 h-2 rounded-full bg-current" />
                {bailStateText}
              </div>

              <h2 className="text-lg font-semibold">Como conectar</h2>
              <ol className="text-sm space-y-1 opacity-80 list-decimal pl-5">
                <li>Clique em <b>Iniciar Sessão</b> abaixo</li>
                <li>Abra o WhatsApp no seu celular</li>
                <li>Vá em <b>Configurações → Aparelhos conectados → Conectar</b></li>
                <li>Aponte a câmera para o QR Code ao lado</li>
                <li>Pronto! A sessão fica ativa 24/7 mesmo com o site fechado.</li>
              </ol>

              <div className="flex gap-2 pt-2 flex-wrap">
                {!bail.connected && (
                  <button
                    onClick={startBaileys}
                    disabled={starting}
                    className="px-4 py-2 bg-[var(--accent)] text-black rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                    data-testid="whatsapp-baileys-start-btn"
                  >
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                    {bail.qr ? "Gerar novo QR" : "Iniciar Sessão"}
                  </button>
                )}
                {(bail.connected || bail.has_session) && (
                  <button
                    onClick={logoutBaileys}
                    disabled={loggingOut}
                    className="px-4 py-2 border border-red-500/40 text-red-400 rounded-sm text-sm hover:bg-red-500/10 disabled:opacity-50 flex items-center gap-2"
                    data-testid="whatsapp-baileys-logout-btn"
                  >
                    {loggingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                    Desconectar
                  </button>
                )}
              </div>

              {bail.last_error && (
                <div className="text-xs text-red-400 mt-2">Último erro: {bail.last_error}</div>
              )}
            </div>
          </div>

          {/* Envio individual pelo Baileys */}
          <div className="border border-[var(--border)] rounded-sm p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Send className="w-4 h-4" />
              Enviar mensagem (via QR Code)
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                className={inp}
                placeholder="Número (11 99999-9999 ou +5511999999999)"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                data-testid="whatsapp-baileys-to-input"
              />
              <input
                className={inp}
                placeholder="Mensagem"
                value={testMsg}
                onChange={(e) => setTestMsg(e.target.value)}
                data-testid="whatsapp-baileys-msg-input"
              />
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={sendBaileys}
                disabled={sending || !bail.connected}
                title={!bail.connected ? "Conecte o WhatsApp primeiro" : ""}
                className="px-4 py-2 bg-[var(--accent)] text-black rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                data-testid="whatsapp-baileys-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar
              </button>
            </div>
          </div>
        </section>
      )}

      {tab === "meta" && (
        <section className="space-y-6" data-testid="whatsapp-meta-panel">
          <div className="border border-[var(--border)] rounded-sm p-6 bg-[var(--bg-elev)]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-sm opacity-70 mb-1">Provedor Oficial</div>
                <h2 className="text-xl font-semibold">Meta WhatsApp Cloud API</h2>
                {meta.configured && (
                  <div className="text-[11px] opacity-60 mt-1">
                    Token: <code>{meta.access_token_masked}</code> · Phone ID: <code>{meta.phone_number_id_masked}</code>
                    <span className="ml-2 opacity-50">(fonte: {meta.source === "user" ? "conta" : meta.source === "env" ? "ambiente" : "-"})</span>
                  </div>
                )}
              </div>
              <div className={`inline-flex items-center gap-2 border px-3 py-1 rounded-sm text-xs ${STATE_STYLE[meta.connected ? "connected" : meta.state === "error" ? "error" : "not_configured"]}`} data-testid="whatsapp-meta-status">
                <span className="w-2 h-2 rounded-full bg-current" />
                {meta.connected
                  ? `Conectado (${meta.phone_number || meta.verified_name || "-"})`
                  : meta.state === "error" ? "Erro"
                  : meta.state === "disabled" ? "Desativada"
                  : "Não configurada"}
              </div>
            </div>
            {meta.error && <p className="text-xs text-red-400 mt-3" data-testid="whatsapp-meta-error">{meta.error}</p>}
          </div>

          <MetaConfigForm meta={meta} onDone={refreshStatus} />

          <div className="border border-[var(--border)] rounded-sm p-6">
            <h3 className="font-semibold mb-4">Enviar mensagem (via Meta API)</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inp} placeholder="Número com DDI (ex: 5511999999999)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="whatsapp-meta-to-input" />
              <input className={inp} placeholder="Mensagem" value={testMsg} onChange={(e) => setTestMsg(e.target.value)} data-testid="whatsapp-meta-msg-input" />
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={sendMeta}
                disabled={sending || !meta.connected}
                title={!meta.connected ? "API Meta não conectada" : ""}
                className="px-4 py-2 bg-[var(--accent)] text-black rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                data-testid="whatsapp-meta-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar via Meta
              </button>
            </div>
          </div>

          <div className="border border-[var(--border)] rounded-sm p-6">
            <h3 className="font-semibold mb-4">Templates aprovados</h3>
            {tplError && <p className="text-xs text-red-400 mb-2">{tplError}</p>}
            {templates.length === 0 ? (
              <p className="text-xs opacity-60">Nenhum template aprovado.</p>
            ) : (
              <ul className="text-sm space-y-2">
                {templates.map((t) => (
                  <li key={`${t.name}-${t.language}`} className="border border-[var(--border)] rounded-sm p-3 flex justify-between items-center">
                    <div>
                      <div className="font-mono text-xs opacity-70">{t.name}</div>
                      <div className="text-[11px] opacity-50">{t.category} · {t.language}</div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                      {t.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {tab === "wame" && (
        <section className="space-y-6" data-testid="whatsapp-wame-panel">
          <div className="border border-[var(--border)] rounded-sm p-6 bg-[var(--bg-elev)]">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-5 h-5 text-[var(--accent)]" />
              <h2 className="text-xl font-semibold">Botão wa.me — abrir conversa rápida</h2>
            </div>
            <p className="text-sm opacity-70">
              Ideal para conversas individuais. Gera um link do WhatsApp com número e mensagem pré-preenchidos —
              você (ou seu cliente) só precisa clicar em <b>Enviar</b> no WhatsApp que abrir.
            </p>
          </div>

          <div className="border border-[var(--border)] rounded-sm p-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inp} placeholder="Número (11 99999-9999)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="whatsapp-wame-to-input" />
              <input className={inp} placeholder="Mensagem pré-preenchida" value={testMsg} onChange={(e) => setTestMsg(e.target.value)} data-testid="whatsapp-wame-msg-input" />
            </div>
            <div className="mt-4 p-3 bg-[var(--bg-elev)] border border-[var(--border)] rounded-sm text-xs font-mono break-all">
              {waMeLink}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <a
                href={waMeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[var(--accent)] text-black rounded-sm text-sm font-semibold hover:opacity-90 flex items-center gap-2"
                data-testid="whatsapp-wame-open-btn"
              >
                <ExternalLink className="w-4 h-4" /> Abrir no WhatsApp
              </a>
              <button
                onClick={copyWaMe}
                className="px-4 py-2 border border-[var(--border)] rounded-sm text-sm hover:bg-[var(--bg-elev)] flex items-center gap-2"
                data-testid="whatsapp-wame-copy-btn"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copiado!" : "Copiar link"}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function MetaConfigForm({ meta, onDone }) {
  const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none text-sm";
  const [token, setToken] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const configured = meta?.configured;
  const enabled = meta?.enabled;

  const save = async () => {
    if (!token.trim() || !phoneId.trim()) return toast.error("Preencha token e Phone Number ID");
    setSaving(true);
    setTestResult(null);
    try {
      await api.post("/whatsapp/meta/configure", {
        access_token: token.trim(),
        phone_number_id: phoneId.trim(),
        enabled: true,
      });
      toast.success("Credenciais Meta salvas e integração ativada!");
      setToken(""); setPhoneId("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar credenciais");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/whatsapp/meta/test");
      setTestResult({ ok: true, ...data });
      toast.success("Conexão OK com a Meta!");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha no teste";
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const toggle = async () => {
    setToggling(true);
    try {
      await api.post("/whatsapp/meta/toggle", { enabled: !enabled });
      toast.success(!enabled ? "Meta API ativada!" : "Meta API desativada.");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alternar");
    } finally {
      setToggling(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Remover as credenciais Meta salvas? Você precisará configurar novamente para usar.")) return;
    setDisconnecting(true);
    try {
      await api.post("/whatsapp/meta/disconnect");
      toast.success("Meta desconectada.");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao desconectar");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="border border-[var(--border)] rounded-sm p-6" data-testid="meta-config-form">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-semibold text-lg">Configuração da API Meta</h3>
        {configured && (
          <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${enabled ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-slate-500/40 text-slate-400 bg-slate-500/10"}`}>
            {enabled ? "ATIVA" : "DESATIVADA"}
          </span>
        )}
      </div>

      {!configured && (
        <div className="mb-4 border border-sky-500/30 bg-sky-500/5 rounded-sm p-3 flex gap-3 text-xs">
          <Info className="w-4 h-4 shrink-0 text-sky-400 mt-0.5" />
          <div className="space-y-1">
            <p><b className="text-sky-300">Como pegar suas credenciais em ~5 min:</b></p>
            <ol className="list-decimal pl-4 space-y-0.5 opacity-80">
              <li>Acesse <a className="underline" href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">developers.facebook.com</a> e faça login com sua conta Meta Business</li>
              <li><b>Meus Apps → Criar App → Negócios</b> e dê um nome (ex: MercadoPagu WhatsApp)</li>
              <li>No painel do app: <b>Adicionar produtos → WhatsApp → Configurar</b> (cria número de teste grátis)</li>
              <li>Menu <b>WhatsApp → API Setup</b>: copie o <b>Access Token</b> temporário e o <b>Phone Number ID</b></li>
              <li>Cole abaixo e clique Salvar. Depois clique Testar Conexão pra validar.</li>
            </ol>
            <p className="opacity-70 mt-1">💡 Para produção, gere um <b>token permanente</b> em Business Settings → System Users.</p>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        <div>
          <label className="text-xs opacity-70 mb-1 block">Access Token</label>
          <div className="relative">
            <input
              type={showToken ? "text" : "password"}
              className={inp + " pr-20"}
              placeholder={configured ? `Salvo (${meta.access_token_masked}) — cole um novo para substituir` : "EAAJZC... (cole o token da Meta aqui)"}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="meta-token-input"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowToken((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-[10px] border border-[var(--border)] rounded-sm hover:bg-[var(--bg-elev)] flex items-center gap-1"
              data-testid="meta-token-toggle-visibility"
            >
              {showToken ? <><EyeOff className="w-3 h-3" /> Ocultar</> : <><Eye className="w-3 h-3" /> Ver</>}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs opacity-70 mb-1 block">Phone Number ID</label>
          <input
            className={inp}
            placeholder={configured ? `Salvo (${meta.phone_number_id_masked})` : "123456789012345"}
            value={phoneId}
            onChange={(e) => setPhoneId(e.target.value.replace(/\D/g, ""))}
            data-testid="meta-phone-id-input"
            inputMode="numeric"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          onClick={save}
          disabled={saving || !token.trim() || !phoneId.trim()}
          className="px-4 py-2 bg-[var(--accent)] text-black rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          data-testid="meta-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {configured ? "Substituir credenciais" : "Salvar e ativar"}
        </button>

        {configured && (
          <>
            <button
              onClick={runTest}
              disabled={testing}
              className="px-4 py-2 border border-sky-500/40 text-sky-400 rounded-sm text-sm hover:bg-sky-500/10 disabled:opacity-50 flex items-center gap-2"
              data-testid="meta-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
              Testar conexão
            </button>
            <button
              onClick={toggle}
              disabled={toggling}
              className={`px-4 py-2 border rounded-sm text-sm disabled:opacity-50 flex items-center gap-2 ${enabled ? "border-amber-500/40 text-amber-400 hover:bg-amber-500/10" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"}`}
              data-testid="meta-toggle-btn"
            >
              {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : enabled ? <ZapOff className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
              {enabled ? "Desativar" : "Ativar"}
            </button>
            <button
              onClick={disconnect}
              disabled={disconnecting}
              className="px-4 py-2 border border-red-500/40 text-red-400 rounded-sm text-sm hover:bg-red-500/10 disabled:opacity-50 flex items-center gap-2"
              data-testid="meta-disconnect-btn"
            >
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Remover credenciais
            </button>
          </>
        )}
      </div>

      {testResult && (
        <div
          className={`mt-4 border rounded-sm p-3 text-xs ${testResult.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-red-500/30 bg-red-500/5 text-red-300"}`}
          data-testid="meta-test-result"
        >
          {testResult.ok ? (
            <div className="space-y-0.5">
              <div><b>✅ Meta respondeu OK!</b></div>
              {testResult.phone_number && <div>📱 Número: <b>{testResult.phone_number}</b></div>}
              {testResult.verified_name && <div>🏢 Nome verificado: <b>{testResult.verified_name}</b></div>}
              {testResult.quality_rating && <div>⭐ Qualidade: <b>{testResult.quality_rating}</b></div>}
            </div>
          ) : (
            <div>❌ {testResult.error}</div>
          )}
        </div>
      )}
    </div>
  );
}
